import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Link,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import axios from 'axios';

const STATUS_OPTIONS = [
  { value: 'new', labelKey: 'aiPhone.statusNew', color: '#4299e1' },
  { value: 'contacted', labelKey: 'aiPhone.statusContacted', color: '#f6ad55' },
  { value: 'done', labelKey: 'aiPhone.statusDone', color: '#48bb78' },
];

function formatDuration(seconds) {
  if (!seconds && seconds !== 0) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('fi-FI', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AiPhoneCallDetailDialog({ open, onClose, callId, onSaved, t }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchCall = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/ai-phone-calls/${callId}/`);
      setCall(response.data);
      setStatus(response.data.status);
      setNotes(response.data.notes || '');
    } catch (error) {
      console.error('Error fetching AI phone call:', error);
    } finally {
      setLoading(false);
    }
  }, [callId]);

  useEffect(() => {
    if (open && callId) {
      fetchCall();
    }
  }, [open, callId, fetchCall]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await axios.patch(`/api/ai-phone-calls/${callId}/`, {
        status,
        notes,
      });
      setSnackbar({ open: true, message: t('aiPhone.saved'), severity: 'success' });
      if (onSaved) onSaved();
    } catch (error) {
      console.error('Error saving AI phone call:', error);
      setSnackbar({ open: true, message: 'Error', severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [callId, status, notes, onSaved, t]);

  const handleStatusChange = useCallback(async (newStatus) => {
    setStatus(newStatus);
    setSaving(true);
    try {
      await axios.patch(`/api/ai-phone-calls/${callId}/`, {
        status: newStatus,
        notes,
      });
      setSnackbar({ open: true, message: t('aiPhone.saved'), severity: 'success' });
      if (onSaved) onSaved();
    } catch (error) {
      console.error('Error updating status:', error);
      setSnackbar({ open: true, message: 'Error', severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [callId, notes, onSaved, t]);

  if (loading || !call) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  const effectiveCallbackNumber = call.effective_callback_number || call.caller_phone;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {call.caller_name || call.caller_phone || t('aiPhone.unknownCaller')}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {/* Status */}
          <Box sx={{ mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t('aiPhone.status')}</InputLabel>
              <Select
                value={status}
                onChange={(e) => handleStatusChange(e.target.value)}
                label={t('aiPhone.status')}
                disabled={saving}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: opt.color }} />
                      {t(opt.labelKey)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Urgency badge */}
          {call.urgency === 'urgent' && (
            <Chip
              label={t('aiPhone.urgencyUrgent')}
              sx={{ bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 600, mb: 2 }}
            />
          )}

          {/* Caller info */}
          <SectionHeader icon={<PersonIcon />} title={t('aiPhone.callerInfo')} />
          <InfoGrid>
            <InfoItem label={t('aiPhone.callerName')} value={call.caller_name || '-'} />
            <InfoItem label={t('aiPhone.callerPhone')} value={
              effectiveCallbackNumber ? (
                <Link href={`tel:${effectiveCallbackNumber}`} underline="hover" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 16 }} />
                  {effectiveCallbackNumber}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    ({t('aiPhone.callBack')})
                  </Typography>
                </Link>
              ) : '-'
            } />
            {call.matched_customer_name && (
              <InfoItem label={t('aiPhone.matchedCustomer')} value={
                <Typography variant="body2" color="primary.main" fontWeight={500}>
                  {call.matched_customer_name}
                </Typography>
              } />
            )}
          </InfoGrid>

          <Divider sx={{ my: 2 }} />

          {/* Vehicle info */}
          <SectionHeader icon={<DirectionsCarIcon />} title={t('aiPhone.vehicleInfo')} />
          <InfoGrid>
            {call.vehicle_registration && (
              <InfoItem label={t('aiPhone.registration')} value={call.vehicle_registration} />
            )}
            {call.vehicle_make && (
              <InfoItem label={t('aiPhone.make')} value={call.vehicle_make} />
            )}
            {call.vehicle_model && (
              <InfoItem label={t('aiPhone.model')} value={call.vehicle_model} />
            )}
            {call.vehicle_year && (
              <InfoItem label={t('aiPhone.year')} value={call.vehicle_year} />
            )}
            {call.matched_vehicle_display && (
              <InfoItem label={t('aiPhone.matchedVehicle')} value={
                <Typography variant="body2" color="primary.main" fontWeight={500}>
                  {call.matched_vehicle_display}
                </Typography>
              } />
            )}
          </InfoGrid>

          {(call.vehicle_registration || call.vehicle_make || call.vehicle_model) && (
            <Divider sx={{ my: 2 }} />
          )}

          {/* Issue info */}
          <SectionHeader title={t('aiPhone.issueInfo')} />
          <InfoGrid>
            <InfoItem label={t('aiPhone.issue')} value={call.issue_description || '-'} fullWidth />
            {call.preferred_timing && (
              <InfoItem label={t('aiPhone.preferredTiming')} value={call.preferred_timing} />
            )}
            <InfoItem label={t('aiPhone.urgency')} value={
              call.urgency === 'urgent'
                ? <Chip size="small" label={t('aiPhone.urgencyUrgent')} sx={{ bgcolor: '#fee2e2', color: '#dc2626' }} />
                : t('aiPhone.urgencyNormal')
            } />
          </InfoGrid>

          <Divider sx={{ my: 2 }} />

          {/* Call info */}
          <SectionHeader title={t('aiPhone.callInfo')} />
          <InfoGrid>
            <InfoItem label={t('aiPhone.calledAt')} value={formatDateTime(call.called_at)} />
            <InfoItem label={t('aiPhone.duration')} value={formatDuration(call.call_duration_seconds)} />
          </InfoGrid>

          {/* Summary */}
          {call.summary && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('aiPhone.summary')}
              </Typography>
              <Typography variant="body2">{call.summary}</Typography>
            </Box>
          )}

          {/* Recording */}
          {call.call_recording_url && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PlayArrowIcon />}
                href={call.call_recording_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('aiPhone.listenRecording')}
              </Button>
            </Box>
          )}

          {/* Transcript */}
          {call.transcript && (
            <Accordion sx={{ mt: 2 }} elevation={0} defaultExpanded={false}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">{t('aiPhone.transcript')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary', fontFamily: 'monospace', fontSize: '13px' }}
                >
                  {call.transcript}
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Notes */}
          <Typography variant="subtitle2" gutterBottom>
            {t('aiPhone.notes')}
          </Typography>
          <TextField
            multiline
            rows={3}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('aiPhone.notesPlaceholder')}
            variant="outlined"
            size="small"
          />
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <CircularProgress size={20} /> : t('aiPhone.save')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

function SectionHeader({ icon, title }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      {icon && React.cloneElement(icon, { sx: { fontSize: 20, color: 'text.secondary' } })}
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
    </Box>
  );
}

function InfoGrid({ children }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
      {children}
    </Box>
  );
}

function InfoItem({ label, value, fullWidth }) {
  return (
    <Box sx={{ gridColumn: fullWidth ? '1 / -1' : 'auto' }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box>
        {typeof value === 'string' ? (
          <Typography variant="body2">{value}</Typography>
        ) : (
          value
        )}
      </Box>
    </Box>
  );
}
